import {ConfigDB} from './configDB.types';
import type {UserEntitlement} from '~/types';

export type AdminUser = {
  superAdmin: boolean;
};

export namespace AISupport {
  export type GetAiSupportingDataResponse = {
    categories: Array<{
      id: string;
      title: string;
    }>;
  };

  export type QuestionSampleSet = {
    id: string;
    label: string;
    created_at: string;
    question_ids: string[];
    data_file_path: string;
  };

  export type QuestionSampleSetQuestionData = {
    id: string;
    dato_record: any; // TODO - add Dato types
    nearest_neighbours: Array<{
      id: string;
      question: any; // TODO - add Dato types
      similarity: number;
    }>;
  };

  export type QuestionMatchingEvaluationResponse = {
    question_id: string;
    candidates: string[];
    best_match_index: number;
    comments?: string;
  };

  export type QuestionMatchingEvaluationResponses = Record<
    string,
    QuestionMatchingEvaluationResponse
  >;

  export type QuestionMatchingEvaluationResponseRow = {
    id: string;
    sample_set_id: number;
    user_id: string;
    created_at: string;
    response: QuestionMatchingEvaluationResponses;
  };

  export type GetQuestionSampleSetsResponse = {
    samples: AISupport.QuestionSampleSet[];
  };

  export type GetQuestionSampleSetDetailResponse = {
    sample: AISupport.QuestionSampleSet;
    questionMatchingEvaluationResponses: Array<
      AISupport.QuestionMatchingEvaluationResponseRow & {user_email: string}
    >;
  };

  export type GetQuestionMatchingEvaluationResponse = {
    questionMatchingEvaluationResponse: AISupport.QuestionMatchingEvaluationResponseRow;
  };

  // New types for question auto match
  export type QuestionMetadataRow = {
    datocms_question_id: string;
    created_at: string;
    updated_at: string;
    first_published_at: string | null;
    published_at: string | null;
    status: string | null;
    current_version: string;
    workflow_stage: string | null;
    attributes: Record<string, any>;
  };

  export type QuestionMatchRow = {
    id: string;
    datocms_user_id: string;
    datocms_question_id: string;
    matched_at: string;
    candidates: string[];
    best_match_question_id: string;
    best_match_index: number;
  } & {
    cosine_distance_current_answer: number;
    best_match_question_metadata: QuestionMetadataRow;
    matchType: string | null;
    perStrategyMatchTypes: Array<{
      decision_id: string;
      decision_strategy: string;
      matchType: string | null;
    }>;
  };

  export type AutoPublishDecisionRow = {
    id: string;
    datocms_question_id: string;
    datocms_record_version: string;
    trigger: string;
    dry_run: boolean;
    match_id: string | null;
    decision_date: string;
    decision_strategy: string;
    decision_stage: string;
    stock_answer_count: number | null;
    llm_gate_decision: string | null;
    llm_gate_reason: string | null;
    system_reason: string | null;
    should_publish: boolean;
    auto_published_at: string | null;
    auto_publish_attempts: number;
    auto_publish_error: string | null;
  };

  export type AutoMatchQuestionRow = {
    question_id: string;
    question_data: QuestionMetadataRow;
    matches: QuestionMatchRow[];
    decisions: AutoPublishDecisionRow[];
  };

  export type GetAutoMatchQuestionsResponse = {
    page: number;
    pageSize: number;
    counts: {
      prodRun: {
        PERFECT_PUBLISH: number;
        FALSE_POSITIVE: number;
        MISSED_OPPORTUNITY: number;
        SAFELY_CAUGHT: number;
        unknown: number;
      };
      perStrategy: Record<
        string,
        {
          PERFECT_PUBLISH: number;
          FALSE_POSITIVE: number;
          MISSED_OPPORTUNITY: number;
          SAFELY_CAUGHT: number;
          unknown: number;
        }
      >;
    };
    questionIds: string[];
    questions: Array<AutoMatchQuestionRow>;
  };

  export type GetAutoMatchQuestionsParams = {
    page: number;
    pageSize: number;
    prodRunMatchType?:
      | 'PERFECT_PUBLISH'
      | 'FALSE_POSITIVE'
      | 'MISSED_OPPORTUNITY'
      | 'SAFELY_CAUGHT';
  };

  // AI Tool Traces
  export type AIToolTraceRow = {
    trace_id: string;
    trace_name: string;
    first_timestamp: string;
    last_timestamp: string;
    entry_count: number;
    error_count: number;
    version: string | null;
  };

  export type AIToolTraceEntryRow = {
    trace_id: string;
    trace_name: string;
    tool_name: string;
    timestamp: string;
    log_level: string;
    message: string;
    data: string | null;
    metadata: string | null;
    version: string | null;
  };

  export type GetAiToolTracesResponse = {
    page: number;
    pageSize: number;
    totalCount: number;
    traces: AIToolTraceRow[];
  };

  export type GetAiToolTracesParams = {
    page: number;
    pageSize: number;
    startDate?: string;
    endDate?: string;
    traceName?: string;
  };

  export type GetAiToolTraceDetailResponse = {
    traceId: string;
    traceName: string;
    entries: AIToolTraceEntryRow[];
  };

  export type GetAiToolTraceDetailParams = {
    traceId: string;
  };

  export type GetAiToolTraceNamesResponse = {
    traceNames: string[];
  };
}

export namespace Reports {
  type PeriodSummary = {
    numQuestionsAsked: number;
    numQuestionsAnswered: number;
    numQuestionsAnsweredWithLinkedContent: number;
    numQuestionsRejected: number;
    averageQueueTimeMinutes: number;
    medianQueueTimeMinutes: number;
    usersAskingXthQuestion: {
      '1st': number;
      '2nd': number;
      '3rd': number;
      '4th': number;
      '5th+': number;
    };
    numQuestionsRated: number;
    avgUserRating: number;
  };

  type QuestionMatchesQualitySummary = {
    numAutoPopulated: number;
    numGoodMatch: number;
    numBadMatch: number;
    numDecideAutoPublished: number;
    numDecideAutoPublishedPerfectPublish: number;
    numDecideAutoPublishedFalsePositive: number;
    numDecideDoNotPublish: number;
    numDecideDoNotPublishMissedOpportunity: number;
    numDecideDoNotPublishSafelyCaught: number;
    numActuallyAutoPublished: number;
    numSkippedAlreadyPublished: number;
    numSkippedEditedSinceDecision: number;
    numSkippedNotFinalisedStage: number;
    numSkippedSensitiveContent: number;
  };

  type DataByPeriod = {
    periodStart: string;
    periodEnd: string;
    summary: PeriodSummary;
    questionMatchesQualitySummary: QuestionMatchesQualitySummary;
    byCategory: Record<string, number>;
    byRejectionReason: Record<string, number>;
    byRatingReason: Record<string, number>;
  };

  export type GetAskStatsV2Params = {
    periodLength: number;
    startDate: string; // yyyy-mm-dd
  };

  export type GetAskStatsV2Response = {
    periods: Array<DataByPeriod>;
    meta: {
      asOfDate: string;
      categories: string[];
      rejectionReasons: string[];
      ratingReasons: string[];
    };
  };

  export type QuestionMatchesQualityDayRow = {
    day: string;
    num_auto_populated: number;
    num_good_match: number;
    num_bad_match: number;
    num_decide_auto_published: number;
    num_decide_auto_published_perfect_publish: number;
    num_decide_auto_published_false_positive: number;
    num_decide_do_not_publish: number;
    num_decide_do_not_publish_missed_opportunity: number;
    num_decide_do_not_publish_safely_caught: number;
    num_actually_auto_published: number;
    num_skipped_already_published: number;
    num_skipped_edited_since_decision: number;
    num_skipped_not_finalised_stage: number;
    num_skipped_sensitive_content: number;
  };

  export type AskMatchQualitySummaryForPeriod = {
    numAutoPopulated: number;
    numGoodMatch: number;
    numBadMatch: number;
    numDecideAutoPublished: number;
    numDecideAutoPublishedPerfectPublish: number;
    numDecideAutoPublishedFalsePositive: number;
    numDecideDoNotPublish: number;
    numDecideDoNotPublishMissedOpportunity: number;
    numDecideDoNotPublishSafelyCaught: number;
    numActuallyAutoPublished: number;
    numSkippedAlreadyPublished: number;
    numSkippedEditedSinceDecision: number;
    numSkippedNotFinalisedStage: number;
    numSkippedSensitiveContent: number;
    percentageGoodMatch: number;
    percentagePerfectPublish: number;
  };

  export type GetQuestionMatchesQualityParams = {
    startDate: string;
    endDate: string;
  };

  export type GetQuestionMatchesQualityResponse = {
    byStrategy: Array<{
      strategy: string;
      color: string;
      data: QuestionMatchesQualityDayRow[];
      summary: AskMatchQualitySummaryForPeriod;
    }>;
  };
}

export namespace Users {
  export type UserListResponse = {
    response: {
      values: {email: string; userId: string}[];
      withEmailCount: number;
      notFound: any[];
    };
  };

  export type UserListParams = {
    type: 'emails' | 'userIds';
    userData: string[];
  };
}

export namespace ParentPay {
  export type Teen = {
    id: string;
    teenFirstName: string;
    teenLastName: string;
    dateOfBirth?: string;
    inviteCode?: string;
    status?: 'pending' | 'invited' | 'claimed' | 'deleted';
    claimedByUserId?: string;
    consentId?: string;
  };

  export type PdfConsentDetails = {
    signedDate: string;
    parentNameOnForm: string;
    teenNameOnForm: string;
    consentFormVersion: string;
    signatureConfirmed: boolean;
    sourceReference: string;
  };

  export type SubmitPdfConsentRequest = {
    parentUserId: string;
    teenId: string;
    pdfConsentDetails: PdfConsentDetails;
  };

  export type SubmitPdfConsentResponse = {
    inviteCode: string;
  };

  export type ResetTeenInviteRequest = {
    parentUserId: string;
    claimedByUserId: string;
  };

  export type EntitlementRenewalDetails = {
    freeTrialStartsAt: string | null;
    freeTrialEndsAt: string | null;
    nextRenewalAt: string;
    autoRenewEnabled: boolean;
  };

  export type EntitlementDetail = {
    entitlement: UserEntitlement;
    planIdentifier: string;
    billingInterval?: 'weekly' | 'monthly' | 'annual';
    renewalDetails?: EntitlementRenewalDetails;
    purchaseSource: 'web' | 'app' | 'granted' | null;
    purchasedBy: 'teen' | 'parent' | null;
    webPurchase: {
      purchaseId: string;
      date: string;
    } | null;
  };

  export type InactiveEntitlementDetail = Omit<
    EntitlementDetail,
    'renewalDetails'
  > & {
    endedAt: string | null;
  };

  export type ParentDetail = {
    userId: string;
    created: string | null;
    email: string;
    emailVerified: boolean;
    isParent: boolean;
    parentProfile: {
      firstName?: string;
      lastName?: string;
    };
    teens: Teen[];
    entitlementsDetail: EntitlementDetail[];
    lastInactiveEntitlement: InactiveEntitlementDetail | null;
  };

  export type ParentLookupRequest = {
    query: string;
  };
}

export namespace Purchases {
  export type WebPurchase = {
    purchaseId: string;
    matchedBy: Array<'email' | 'redeemed'>;
    date: string;
    type: 'one_time' | 'subscription';
    productName: string | null;
    productId: string;
    promotionCode: string | null;
    isLiveMode: boolean;
    buyerEmail: string;
    linkedUserId: string | null;
    subscription: {
      status: 'active' | 'inactive';
      activeUntil: string | null;
      nextRenewalAt: string | null;
      autoRenewEnabled: boolean | null;
    } | null;
    codes: Array<{
      code: string;
      status: 'created' | 'redeemed';
      redeemedDate: string | null;
      redeemedByUserId: string | null;
    }>;
  };

  export type WebPurchasesLookupRequest = {
    query: string;
  };

  export type WebPurchasesLookupResponse = {
    email: string | null;
    hasAccount: boolean;
    purchases: WebPurchase[];
  };
}

export namespace Subscriptions {
  export type PremiumDataResponse = {
    lifetimeCount: number;
    parentPayTeenCount: number;
    premiumCount: number;
    subscriptionTotalCount: number;
  };

  export type DiscountCode = {
    id: string;
    code: string;
    discountType: string;
    active: boolean;
    createdAt: string;
  };

  export type GetDiscountCodesResponse = {
    discountCodes: Array<DiscountCode>;
  };

  export type CreateDiscountCodeRequest = {
    id: string;
    code: string;
    discountType: string;
    active: boolean;
  };

  export type UpdateDiscountCodeRequest = {
    id: string;
    code: string;
    active: boolean;
  };

  export type PutDiscountCodeResponse = {
    discountCode: DiscountCode;
  };

  export type GetDiscountCodesTrackingResponse = {
    discountCodes: Record<
      string,
      {
        subscriptions: number;
        subscriptionsSandbox: number;
      }
    >;
  };

  export type CreateAccessCodesRequest = {
    client: string | null;
    notes: string | null;
    entitlement: UserEntitlement;
    durationDays: number;
    quantity: number;
  };

  export type CreateAccessCodesResponse = {
    success: boolean;
    redemptionCodes: string[];
  };
}

export namespace Config {
  export type GetConfigResponseData = {
    sysAdminConfig: {
      purchasely: {
        productPlanEntitlements: ConfigDB.Sysadmin.Purchasely.ProductPlanEntitlements;
        discountTypes: ConfigDB.Sysadmin.Purchasely.DiscountTypes;
      };
      askLunaAutoPublish: ConfigDB.Sysadmin.AskLunaAutoPublish;
    };
  };

  type UpdatePurchaselyDiscountTypeRequestData = {
    config: 'purchasely/discountTypes';
    action: 'create' | 'update';
    data: {
      discountType: string;
      audience: string;
      title?: string;
      description?: string;
    };
  };

  type UpdateAskLunaAutoPublishRequestData = {
    config: 'askLunaAutoPublish';
    action: 'update';
    data: {
      enabled: boolean;
    };
  };

  export type UpdateConfigRequestData =
    | UpdatePurchaselyDiscountTypeRequestData
    | UpdateAskLunaAutoPublishRequestData;
}

export namespace Migrations {
  export enum MigrationName {
    migrateDatoReferencesToCollection = 'migrateDatoReferencesToCollection',
    migrateArticleReferencesToCollection = 'migrateArticleReferencesToCollection',
    migrateParentArticleReferencesToCollection = 'migrateParentArticleReferencesToCollection',
    migrateQuestionWorkflowStageReviewed = 'migrateQuestionWorkflowStageReviewed',
    migrateAskLunaRatings = 'migrateAskLunaRatings',
  }

  export type MigrationError = {
    message: string;
    data?: any;
  };

  export type MigrationRowError = MigrationError & {
    recordId: string;
  };

  export enum MigrationRunStatus {
    created = 'created',
    scanning = 'scanning',
    queued = 'queued',
    processing = 'processing',
    completed = 'completed',
    failed = 'failed',
  }

  export type MigrationRunDocument<
    TParams extends Record<string, any> = Record<string, any>,
  > = {
    id: string;
    name: MigrationName;
    status: MigrationRunStatus;
    createdAt: string;
    completedAt: string | null;
    initialCursor: string | null;
    lastScannedCursor: string | null;
    params: TParams;
    maxConcurrentBatches: number;
    batchStatusCounts: Partial<Record<MigrationBatchStatus, number>>;
    rowStatusCounts: {
      created: number;
      total: number;
      success: number;
      error: number;
    };
  };

  export enum MigrationBatchStatus {
    created = 'created',
    queued = 'queued',
    processing = 'processing',
    completed = 'completed',
    failed = 'failed',
  }

  export type MigrationBatchDocument<TBatchData = any> = {
    id: string;
    migrationRunId: string;
    migrationName: MigrationName;
    status: MigrationBatchStatus;
    createdAt: string;
    startedAt: string | null;
    completedAt: string | null;
    errors?: Array<MigrationError | MigrationRowError>;
    rowStatusCounts: {
      total: number;
      success: number;
      error: number;
    };
    data: TBatchData;
  };

  export type MigrationRowDocument<TData = any> = {
    migrationRunId: string;
    migrationName: MigrationName;
    migratedId: string;
    success: boolean;
    processedAt: string;
    error?: MigrationRowError;
    dataBefore: TData;
    dataAfter: TData;
  };

  export type MigrationRunDocumentApi = Omit<
    MigrationRunDocument,
    'createdAt' | 'completedAt'
  > & {
    id: string;
    createdAt: string;
    completedAt: string | null;
    logsLink: string;
    firestoreLink: string;
  };

  export type GetMigrationRunsResponse = Array<MigrationRunDocumentApi>;

  export type CreateMigrationRunRequest = {
    name: MigrationName;
    params: Record<string, any>;
  };

  export type CreateMigrationRunResponse = {
    id: string;
  };

  export type GetMigrationRunDetailResponse = MigrationRunDocumentApi & {
    batches: MigrationBatchDocument[];
  };

  export type RetryFailedMigrationRequest = {
    runId: string;
  };

  export type StartMigrationRunRequest = {
    runId: string;
    action: 'start' | 'scan' | 'stop';
  };
}

export namespace SystemHealth {
  export type RagStatus =
    | 'green'
    | 'amber'
    | 'red'
    | 'unavailable'
    | 'warming'
    | 'dormant';

  export type RagThresholds = {
    redRatio: number;
    amberRatio: number;
    minVolumeForRed: number;
    backupStaleHours: number;
  };

  export type SystemHealthHistoryPoint = {
    date: string; // YYYY-MM-DD report date (window = date-1 08:00 → date 08:00 UK)
    value: number | null;
    status: RagStatus;
    avg7: number | null;
    avg30: number | null;
    ratio: number | null;
    deltaPct: number | null;
    note?: string;
    error?: string;
    backfilled?: boolean;
  };

  /** where a metric's number comes from — mirrors MetricSource in luna-firebase systemHealth.types.ts */
  export type MetricSource = {
    system: 'Firestore' | 'BigQuery' | 'Mixpanel';
    from: string;
    counts: string;
    window?: string;
    caveats?: string[];
  };

  export type SystemHealthHistoryMetric = {
    key: string;
    label: string;
    source: MetricSource;
    disabled: boolean;
    thresholds: RagThresholds;
    points: SystemHealthHistoryPoint[];
  };

  export type SystemHealthConfig = {
    enabled?: boolean;
    thresholds?: Partial<RagThresholds>;
    perMetric?: Record<string, Partial<RagThresholds> & {disabled?: boolean}>;
  };

  export type GetSystemHealthHistoryParams = {
    days: number;
  };

  export type GetSystemHealthHistoryResponse = {
    days: number;
    from: string | null;
    to: string | null;
    windowAnchor: string;
    dates: string[];
    defaults: RagThresholds;
    minSnapshotsForRag: number;
    config: SystemHealthConfig;
    metrics: SystemHealthHistoryMetric[];
  };
}

export namespace AuditLog {
  export type Entry = {
    userId: string;
    userEmail: string | null;
    endpoint: string;
    details: Record<string, unknown> | null;
    timestamp: string;
  };

  export type GetAuditLogParams = {
    endpoints: string[];
  };

  export type GetAuditLogResponse = {
    logs: Entry[];
  };
}
